import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { 
  ListingCondition,
  PickupOption,
  getConditionDisplayText, 
  getPickupOptionDisplayText 
} from '@/types/residents/marketplaceListing';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

const DeliveryIcon = RESIDENTS_SVG_COMPONENTS.DELIVERY;

interface ItemDetailsSectionProps {
  price: string;
  title: string;
  description: string;
  condition: ListingCondition;
  pickupOption: PickupOption;
  isSold?: boolean;
}

/**
 * Reusable item details section showing price, title, description, and pickup option
 */
export const ItemDetailsSection: React.FC<ItemDetailsSectionProps> = ({
  price,
  title,
  description,
  condition,
  pickupOption,
  isSold = false,
}) => (
  <View style={styles.container}>
    {/* Price and Condition */}
    <View style={styles.priceRow}>
      <View style={styles.priceContainer}>
        <Text style={styles.price}>{price}</Text>
        {isSold && (
          <>
            <Text style={styles.priceDot}> • </Text>
            <Text style={styles.soldText}>Sold</Text>
          </>
        )}
      </View>
      <Text style={styles.condition}>{getConditionDisplayText(condition)}</Text>
    </View>

    {/* Title */}
    <Text style={styles.title}>{title}</Text>

    {/* Description */}
    <Text style={styles.description}>{description}</Text>

    {/* Pickup Option Badge */}
    <View style={styles.pickupBadge}>
      <DeliveryIcon width={16} height={16} color={MARKETPLACE_COLORS.textSecondary} />
      <Text style={styles.pickupText}>{getPickupOptionDisplayText(pickupOption)}</Text>
    </View>
  </View>
);

const styles = StyleSheet.create({
  container: {
    gap: 6,
  },
  priceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  priceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  price: {
    fontSize: 24,
    fontWeight: '700',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 36,
  },
  priceDot: {
    fontSize: 24,
    fontWeight: '700',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 36,
  },
  soldText: {
    fontSize: 24,
    fontWeight: '500',
    color: '#FF3333',
    lineHeight: 36,
  },
  condition: {
    fontSize: 12,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 18,
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 24,
  },
  description: {
    fontSize: 14,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 21,
  },
  pickupBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: MARKETPLACE_COLORS.white5Percent,
    paddingHorizontal: 12,
    height: 28,
    borderRadius: 20,
    alignSelf: 'flex-start',
    marginTop: 10,
  },
  pickupText: {
    fontSize: 12,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 18,
  },
});

